import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE_URL } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { useStreamingSse } from '@/hooks/use-streaming-sse'

export type SimulationPack = {
  methodology_id: string
  client_config: string
  label: string
  description: string
  supports_synthetic_profiles: boolean
  supports_cv_upload: boolean
  supports_hire_events: boolean
  supports_demo_pack?: boolean
  supports_haystack_pack?: boolean
  subject_noun?: string
  pool_noun?: string
  artifact_noun?: string
  page_title?: string
  page_description?: string
  empty_pack_message?: string
}

export type SimulationProfile = {
  id: string
  first_name: string
  last_name: string
  email: string
  headline?: string
  is_needle: boolean
  final_score?: number | null
  rank?: number | null
  evaluation_id?: string | null
  scored_at?: string | null
  enrich_status?: string | null
  enrich_summary?: string
  skill_count?: number
  experience_count?: number
  recent_role_title?: string
  recent_role_company?: string
}

export type SimulationCondition = {
  id: string
  name: string
  patient_profile_id?: string | null
  patient_name?: string
  evaluation_id?: string | null
  final_score?: number | null
  rank?: number | null
  scored_at?: string | null
}

export type SimulationScoreSummary = {
  evaluated: number
  total: number
  errors: string[]
  top_profile_id?: string | null
  top_condition_id?: string | null
}

export type SimulationScenario = {
  id: string
  name: string
  methodology_id: string
  client_config: string
  status: string
  organisation_id?: string | null
  organisation_slug?: string | null
  organisation_name?: string | null
  opportunity_id?: string | null
  opportunity_title?: string | null
  job_description?: string
  opportunity_skills?: SimulationOpportunitySkill[]
  profile_ids: string[]
  needle_profile_ids: string[]
  profiles: SimulationProfile[]
  conditions?: SimulationCondition[]
  active_for_rubric_preview: boolean
  active_job?: SimulationActiveJob | null
  last_score_summary?: SimulationScoreSummary | null
  pool_cohort_summary?: {
    summary?: string
    summary_at?: string | null
    top_recommendations?: Array<{
      profile_id: string
      candidate_name: string
      status?: string
      final_score?: number | null
      rank?: number | null
    }>
    fingerprint?: string | null
  } | null
  created_at?: string | null
  updated_at?: string | null
  completeness_log?: {
    opportunity?: { ok?: boolean; misses?: string[] }
    opening?: { ok?: boolean; misses?: string[] }
    profiles?: Record<string, { ok?: boolean; misses?: string[] }>
  } | null
  metadata?: Record<string, unknown>
}

export type SimulationCapabilities = {
  http_trigger_enabled: boolean
  hire_events_enabled?: boolean
  craft_enabled?: boolean
  demo_pack_enabled?: boolean
  alpha_pack_enabled?: boolean
  haystack_pack_enabled?: boolean
  packs: SimulationPack[]
  ui_mutate_hint?: string
}

export type SimulationActiveJob = {
  id: string
  kind: string
  status: string
  progress?: { done?: number; total?: number; current_ids?: string[] } | null
}

export type SimulationJob = {
  id: string
  kind: string
  status: string
  scenario_id?: string | null
  client_config?: string | null
  progress?: { done?: number; total?: number; current_ids?: string[] }
  error?: string | null
  result?: {
    scenario_id?: string
    [key: string]: unknown
  } | null
}

export type SimulationOpportunitySkill = {
  skill_id: string
  skill_name: string
  requirement_type: 'required' | 'preferred' | string
}

export type SimulationHaystackTest = {
  test_id: string
  job_title: string
  candidate_count: number
  label?: string
}

export type SimulationJobKind =
  | 'generate'
  | 'pass1'
  | 'enrich'
  | 'demo_pack'
  | 'alpha_pack'
  | 'hire_events'
  | 'extract_job_spec'
  | 'haystack_pack'

export function isSimulationJobActive(status?: string | null): boolean {
  return status === 'queued' || status === 'running'
}

async function readError(res: Response): Promise<string> {
  const text = await res.text()
  try {
    const json = JSON.parse(text) as { detail?: string; error?: string }
    return json.detail || json.error || text || res.statusText
  } catch {
    return text || res.statusText
  }
}

export function useSimulationCapabilities() {
  return useQuery({
    queryKey: ['platform-sim-capabilities'],
    queryFn: async (): Promise<SimulationCapabilities> => {
      const res = await authorizedFetch('/api/evaluations/simulation/capabilities')
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

export function useSimulationScenarios() {
  return useQuery({
    queryKey: ['platform-sim-scenarios'],
    queryFn: async (): Promise<SimulationScenario[]> => {
      const res = await authorizedFetch('/api/evaluations/simulation/scenarios')
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

export function useSimulationScenario(scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['platform-sim-scenario', scenarioId],
    enabled: Boolean(scenarioId),
    queryFn: async (): Promise<SimulationScenario> => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId!)}`,
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

export function useCreateSimulationScenario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name: string
      methodology_id?: string
      job_title?: string
      job_description?: string
      organisation_slug?: string
      synthetic_profile_count?: number
    }) => {
      const res = await authorizedFetch('/api/evaluations/simulation/scenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
    },
  })
}

function invalidateScenario(qc: ReturnType<typeof useQueryClient>, scenarioId: string) {
  void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
  void qc.invalidateQueries({ queryKey: ['platform-sim-scenario', scenarioId] })
}

export function useArchiveSimulationScenario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scenarioId: string) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}`,
        { method: 'DELETE' },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<{ ok: boolean; id: string; status: string }>
    },
    onSuccess: (_data, scenarioId) => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
      qc.removeQueries({ queryKey: ['platform-sim-scenario', scenarioId] })
    },
  })
}

export function useUpdateSimulationJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scenarioId,
      title,
      description,
      skills,
    }: {
      scenarioId: string
      title?: string
      description?: string
      skills?: SimulationOpportunitySkill[]
    }) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/job`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, description, skills }),
        },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useGenerateSimulationProfiles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scenarioId,
      count,
      async_job = true,
    }: {
      scenarioId: string
      count: number
      async_job?: boolean
    }) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/profiles/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count, async_job }),
        },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob | SimulationScenario>
    },
    onSuccess: (_data, vars) => invalidateScenario(qc, vars.scenarioId),
  })
}

export function useUploadSimulationCv() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ scenarioId, file }: { scenarioId: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/profiles/upload-cv`,
        { method: 'POST', body },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useUploadSimulationCvs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ scenarioId, files }: { scenarioId: string; files: File[] }) => {
      const body = new FormData()
      for (const file of files) {
        body.append('files', file)
      }
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/profiles/upload-cvs`,
        { method: 'POST', body },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useUploadSimulationJobFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ scenarioId, file }: { scenarioId: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/job/upload`,
        { method: 'POST', body },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useSetSimulationNeedles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scenarioId,
      needle_profile_ids,
    }: {
      scenarioId: string
      needle_profile_ids: string[]
    }) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/needles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ needle_profile_ids }),
        },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useMarkSimulationRubricPreview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scenarioId: string) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/use-for-rubric-preview`,
        { method: 'POST' },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationScenario>
    },
    onSuccess: (data) => invalidateScenario(qc, data.id),
  })
}

export function useRunSimulationPass1() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scenarioId,
      async_job = true,
    }: {
      scenarioId: string
      async_job?: boolean
    }) => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/pass1`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ async_job }),
        },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob>
    },
    onSuccess: (_data, vars) => invalidateScenario(qc, vars.scenarioId),
  })
}

export function useRunSimulationHireEvents() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      scenarioId: string
      apply_count?: number
      invite_count?: number
      profile_ids?: string[]
      async_job?: boolean
    }) => {
      const { scenarioId, ...body } = payload
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/hire-events`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            apply_count: body.apply_count ?? 0,
            invite_count: body.invite_count ?? 0,
            profile_ids: body.profile_ids,
            async_job: body.async_job ?? true,
          }),
        },
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob>
    },
    onSuccess: (_data, vars) => invalidateScenario(qc, vars.scenarioId),
  })
}

export function useSimulationJob(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['platform-sim-job', jobId],
    enabled: Boolean(jobId) && enabled,
    retry: (failureCount, error) => {
      if (error instanceof Error && /404|not found/i.test(error.message)) return false
      return failureCount < 1
    },
    queryFn: async (): Promise<SimulationJob> => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/jobs/${encodeURIComponent(jobId!)}`,
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

type JobStreamEvent = {
  seq?: number
  job?: SimulationJob
  people?: SimulationProfile[]
  done?: number
  total?: number
  kind?: string
  status?: string
  current_ids?: string[]
  error?: string | null
  id?: string
  first_name?: string
  last_name?: string
  name?: string
  headline?: string
  skill_count?: number
  experience_count?: number
  recent_role_title?: string
  recent_role_company?: string
  enrich_status?: string | null
  enrich_summary?: string
  score?: number | null
  evaluation_id?: string | null
}

function mergeStreamPerson(
  current: SimulationProfile | undefined,
  event: JobStreamEvent,
): SimulationProfile {
  const id = event.id || current?.id || ''
  return {
    id,
    first_name: event.first_name ?? current?.first_name ?? '',
    last_name: event.last_name ?? current?.last_name ?? '',
    email: current?.email ?? '',
    headline: event.headline ?? current?.headline ?? '',
    is_needle: current?.is_needle ?? false,
    final_score: event.score ?? current?.final_score ?? null,
    rank: current?.rank ?? null,
    evaluation_id: event.evaluation_id ?? current?.evaluation_id ?? null,
    scored_at: current?.scored_at ?? null,
    enrich_status: event.enrich_status ?? current?.enrich_status ?? null,
    enrich_summary: event.enrich_summary ?? current?.enrich_summary ?? '',
    skill_count: event.skill_count ?? current?.skill_count ?? 0,
    experience_count: event.experience_count ?? current?.experience_count ?? 0,
    recent_role_title: event.recent_role_title ?? current?.recent_role_title ?? '',
    recent_role_company: event.recent_role_company ?? current?.recent_role_company ?? '',
  }
}

export function useSimulationJobStream(jobId: string | undefined, scenarioId?: string) {
  const { token } = useAuth()
  const qc = useQueryClient()
  const enabled = Boolean(jobId && token)
  const url = jobId
    ? `${API_BASE_URL || ''}/api/evaluations/simulation/jobs/${encodeURIComponent(jobId)}/stream`
    : ''

  const onMessage = useCallback(
    (message: { event?: string; data: string }) => {
      if (!jobId || !message.data) return
      let payload: JobStreamEvent
      try {
        payload = JSON.parse(message.data) as JobStreamEvent
      } catch {
        return
      }
      const eventName = message.event || 'message'

      if (eventName === 'job_snapshot' && payload.job) {
        qc.setQueryData(['platform-sim-job', jobId], payload.job)
        if (scenarioId && payload.people) {
          qc.setQueryData(['platform-sim-scenario', scenarioId], (current: SimulationScenario | undefined) => {
            if (!current) return current
            return {
              ...current,
              profiles: payload.people ?? current.profiles,
              profile_ids: (payload.people ?? current.profiles).map((row) => row.id),
              active_job: {
                id: payload.job!.id,
                kind: payload.job!.kind,
                status: payload.job!.status,
                progress: payload.job!.progress,
              },
            }
          })
        }
        return
      }

      if (eventName === 'job_progress') {
        qc.setQueryData(['platform-sim-job', jobId], (current: SimulationJob | undefined) => ({
          id: jobId,
          kind: payload.kind || current?.kind || '',
          status: payload.status || current?.status || 'running',
          scenario_id: current?.scenario_id,
          progress: {
            done: payload.done,
            total: payload.total,
            current_ids: payload.current_ids,
          },
          error: current?.error ?? null,
          result: current?.result ?? null,
        }))
        return
      }

      if ((eventName === 'person_upsert' || eventName === 'person_enrich') && payload.id && scenarioId) {
        qc.setQueryData(['platform-sim-scenario', scenarioId], (current: SimulationScenario | undefined) => {
          if (!current) return current
          const existing = current.profiles ?? []
          const index = existing.findIndex((row) => row.id === payload.id)
          const nextPerson = mergeStreamPerson(index >= 0 ? existing[index] : undefined, payload)
          const profiles =
            index >= 0
              ? existing.map((row, i) => (i === index ? nextPerson : row))
              : [...existing, nextPerson]
          return {
            ...current,
            profiles,
            profile_ids: profiles.map((row) => row.id),
          }
        })
        if (eventName === 'person_enrich') {
          void qc.invalidateQueries({ queryKey: ['profile', payload.id] })
        }
        return
      }

      if (eventName === 'job_completed' || eventName === 'job_failed') {
        if (payload.job) {
          qc.setQueryData(['platform-sim-job', jobId], payload.job)
        } else {
          qc.setQueryData(['platform-sim-job', jobId], (current: SimulationJob | undefined) => ({
            id: jobId,
            kind: current?.kind || '',
            status: eventName === 'job_completed' ? 'completed' : 'failed',
            scenario_id: current?.scenario_id,
            progress: current?.progress,
            error: payload.error ?? current?.error ?? null,
            result: current?.result ?? null,
          }))
        }
        if (scenarioId) {
          void qc.invalidateQueries({ queryKey: ['platform-sim-scenario', scenarioId] })
          void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
        }
      }
    },
    [jobId, qc, scenarioId],
  )

  return useStreamingSse({
    url,
    token,
    enabled,
    onMessage,
  })
}

export function useCreateSimulationDemoPack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name?: string
      methodology_id?: string
      org_count?: number
      jobs_per_org?: number
      people_count?: number
      patient_count?: number
      conditions_per_patient?: number
      needle_count?: number
      async_job?: boolean
    }) => {
      const res = await authorizedFetch('/api/evaluations/simulation/demo-packs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name,
          methodology_id: payload.methodology_id,
          org_count: payload.org_count ?? 10,
          jobs_per_org: payload.jobs_per_org ?? 2,
          people_count: payload.people_count ?? 36,
          patient_count: payload.patient_count ?? 12,
          conditions_per_patient: payload.conditions_per_patient ?? 2,
          needle_count: payload.needle_count ?? 3,
          async_job: payload.async_job ?? true,
        }),
      })
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
    },
  })
}

export function useCreateSimulationAlphaPack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name?: string
      org_count?: number
      jobs_per_org?: number
      people_count?: number
      featured_count?: number
      apply_count?: number
      invite_count?: number
      replace?: boolean
      async_job?: boolean
    }) => {
      const res = await authorizedFetch('/api/evaluations/simulation/alpha-packs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name,
          org_count: payload.org_count ?? 6,
          jobs_per_org: payload.jobs_per_org ?? 2,
          people_count: payload.people_count ?? 24,
          featured_count: payload.featured_count ?? 3,
          apply_count: payload.apply_count ?? 8,
          invite_count: payload.invite_count ?? 4,
          replace: payload.replace ?? false,
          async_job: payload.async_job ?? true,
        }),
      })
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
    },
  })
}

export type SimulationGeneratedRow = {
  id: string
  kind: string
  label: string
  marked: boolean
}

export type SimulationGenerated = {
  profiles: SimulationGeneratedRow[]
  organisations: SimulationGeneratedRow[]
  opportunities: SimulationGeneratedRow[]
  applications: SimulationGeneratedRow[]
}

export type SimulationPurgeSelection = {
  profile_ids?: string[]
  organisation_ids?: string[]
  opportunity_ids?: string[]
  application_ids?: string[]
}

export type SimulationPurgePreview = {
  would_delete: Array<{ id: string; kind: string; label: string }>
  refused: Array<{ id: string; kind: string; reason: string }>
  counts: Record<string, number>
  deleted?: boolean
}

export function useSimulationGenerated(scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['platform-sim-generated', scenarioId],
    enabled: Boolean(scenarioId),
    queryFn: async (): Promise<SimulationGenerated> => {
      const res = await authorizedFetch(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId || '')}/generated`,
      )
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

async function postPurge(path: string, body?: SimulationPurgeSelection): Promise<SimulationPurgePreview> {
  const res = await authorizedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error(await readError(res))
  return res.json()
}

export function usePreviewScenarioPurge(scenarioId: string) {
  return useMutation({
    mutationFn: (selection: SimulationPurgeSelection) =>
      postPurge(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/purge/preview`,
        selection,
      ),
  })
}

export function usePurgeScenarioRows(scenarioId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (selection: SimulationPurgeSelection) =>
      postPurge(
        `/api/evaluations/simulation/scenarios/${encodeURIComponent(scenarioId)}/purge`,
        selection,
      ),
    onSuccess: () => {
      invalidateScenario(qc, scenarioId)
      void qc.invalidateQueries({ queryKey: ['platform-sim-generated', scenarioId] })
    },
  })
}

export function usePreviewTenantPurge() {
  return useMutation({
    mutationFn: () => postPurge('/api/evaluations/simulation/purge/preview'),
  })
}

export function usePurgeTenantRows() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => postPurge('/api/evaluations/simulation/purge'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
      void qc.invalidateQueries({ queryKey: ['platform-sim-generated'] })
    },
  })
}

export function useHaystackTests(enabled: boolean) {
  return useQuery({
    queryKey: ['platform-sim-haystack-tests'],
    enabled,
    queryFn: async (): Promise<SimulationHaystackTest[]> => {
      const res = await authorizedFetch('/api/evaluations/simulation/haystack-packs')
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

export function useCreateSimulationHaystackPack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { test_id: string; name?: string; async_job?: boolean }) => {
      const res = await authorizedFetch('/api/evaluations/simulation/haystack-packs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test_id: payload.test_id,
          name: payload.name,
          async_job: payload.async_job ?? true,
        }),
      })
      if (!res.ok) throw new Error(await readError(res))
      return res.json() as Promise<SimulationJob>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
    },
  })
}

export type SimulationModelCounts = Record<string, number>

export type SimulationCleanupRun = {
  id: string
  action: 'flag' | 'delete' | 'mark_selected' | 'delete_selected'
  status: 'succeeded' | 'failed'
  actor_email: string
  counts: SimulationModelCounts
  targets: SimulationBulkRow[]
  detail: string
  created_at: string
}

export type SimulationCleanupOrganisation = { id: string; name: string; reason: string }

export type SimulationCleanupSummary = {
  flagged: SimulationModelCounts
  would_flag: SimulationModelCounts
  would_flag_skipped: SimulationModelCounts
  would_flag_organisations: SimulationCleanupOrganisation[]
  skipped_organisations: SimulationCleanupOrganisation[]
  would_delete: SimulationModelCounts
  would_delete_refused: number
  flagged_users_without_profile: number
  unmatched_reserved_email_users: number
  delete_confirmation: string
  runs: SimulationCleanupRun[]
}

const CLEANUP_KEY = ['platform-sim-cleanup']

export function useSimulationCleanup() {
  return useQuery({
    queryKey: CLEANUP_KEY,
    queryFn: async (): Promise<SimulationCleanupSummary> => {
      const res = await authorizedFetch('/api/evaluations/simulation/cleanup')
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
  })
}

function useCleanupAction<TArgs>(path: string, body: (args: TArgs) => unknown) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: TArgs): Promise<SimulationCleanupRun> => {
      const res = await authorizedFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body(args) ?? {}),
      })
      if (!res.ok) throw new Error(await readError(res))
      return res.json()
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLEANUP_KEY })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
      void qc.invalidateQueries({ queryKey: ['admin-organisations'] })
      void qc.invalidateQueries({ queryKey: ['platform-sim-scenarios'] })
    },
  })
}

export function useFlagSimulationRows() {
  return useCleanupAction<void>('/api/evaluations/simulation/cleanup/flag', () => ({}))
}

export function useDeleteSimulatedRows() {
  return useCleanupAction<string>('/api/evaluations/simulation/cleanup/delete', (confirmation) => ({
    confirmation,
  }))
}

export type SimulationBulkKind = 'user' | 'organisation'
export type SimulationBulkAction = 'mark' | 'delete'

export type SimulationBulkRow = {
  kind: SimulationBulkKind
  id: string
  label: string
  changes: boolean
  reason: string
  warning: string
}

export type SimulationBulkPreview = {
  kind: SimulationBulkKind
  action: SimulationBulkAction
  rows: SimulationBulkRow[]
  will_change: number
  refused: number
}

type SimulationBulkRequest = {
  kind: SimulationBulkKind
  action: SimulationBulkAction
  ids: string[]
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await authorizedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(await readError(res))
  return res.json()
}

export function usePreviewSimulationSelection() {
  return useMutation({
    mutationFn: (request: SimulationBulkRequest) =>
      postJson<SimulationBulkPreview>('/api/evaluations/simulation/cleanup/selection/preview', request),
  })
}

export function useApplySimulationSelection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: SimulationBulkRequest) =>
      postJson<SimulationCleanupRun>('/api/evaluations/simulation/cleanup/selection', {
        ...request,
        confirmed: true,
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLEANUP_KEY })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
      void qc.invalidateQueries({ queryKey: ['admin-organisations'] })
    },
  })
}
